// ─── Staff users & roles (RBAC stage a, 2026-07-16) ─────────────────────────
// Admin-only management of STAFF accounts: create (email + password + name +
// WhatsApp + roles), edit roles, activate/deactivate, reset password.
// Passwords are handled by Supabase Auth (bcrypt) — never displayed after
// creation, never logged. Permission ENFORCEMENT ships in stage (b); until
// then staff accounts authenticate but the admin API blocks them (safe).
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AdminLayout } from '@/components/AdminLayout';
import { Button } from '@/components/ui/Button';
import { usersApi, uploadToSignedUrl, type StaffUser, type StaffRole } from '@/lib/api';
import { useT } from '@/lib/i18n';

const inputCls =
  'w-full px-3 py-2 border border-gray-200 rounded-lg text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white';

function genPassword(): string {
  const chars = 'abcdefghjkmnpqrstuvwxyzABCDEFGHJKMNPQRSTUVWXYZ23456789';
  let out = '';
  const rand = new Uint32Array(14);
  crypto.getRandomValues(rand);
  for (const r of rand) out += chars[r % chars.length];
  return out;
}

function RoleChips({ roles }: { roles: StaffRole[] }) {
  if (!roles.length) return <span className="text-[11px] text-gray-400">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {roles.map((r) => (
        <span
          key={r.id}
          className={r.slug === 'admin'
            ? 'px-2 py-0.5 rounded-full text-[10.5px] font-semibold text-adm-gold-ink'
            : 'px-2 py-0.5 rounded-full text-[10.5px] font-semibold border bg-[#EEF2FF] text-[#4338CA] border-[#C7D2FE] dark:bg-indigo-500/15 dark:text-[#A5B4FC] dark:border-indigo-400/40'}
          style={r.slug === 'admin' ? { background: '#C9A24A22', border: '1px solid #C9A24A55' } : undefined}
        >
          {r.name}
        </span>
      ))}
    </div>
  );
}

function RoleCheckboxes({
  allRoles, selected, onChange,
}: { allRoles: StaffRole[]; selected: Set<string>; onChange: (next: Set<string>) => void }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
      {allRoles.map((r) => (
        <label key={r.id} className="flex items-start gap-2 p-2 rounded-lg border border-gray-100 hover:border-brand-300 cursor-pointer bg-white">
          <input
            type="checkbox"
            className="mt-0.5 accent-[#C9A24A]"
            checked={selected.has(r.id)}
            onChange={(e) => {
              const next = new Set(selected);
              if (e.target.checked) next.add(r.id); else next.delete(r.id);
              onChange(next);
            }}
          />
          <span>
            <span className="block text-[12.5px] font-semibold text-gray-800">{r.name}</span>
            {r.description && <span className="block text-[11px] text-gray-400 leading-tight">{r.description}</span>}
          </span>
        </label>
      ))}
    </div>
  );
}

function CreateUserCard({ allRoles, onDone }: { allRoles: StaffRole[]; onDone: () => void }) {
  const { t } = useT();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState(genPassword());
  const [showPw, setShowPw] = useState(true);
  const [fullName, setFullName] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [commission, setCommission] = useState('');
  const [roleIds, setRoleIds] = useState<Set<string>>(new Set());

  const createMut = useMutation({
    mutationFn: () => usersApi.create({
      email, password, full_name: fullName, whatsapp: whatsapp || undefined, role_ids: [...roleIds],
      // Omitted entirely when blank so the account falls back to the global
      // rate. Sending 0 for a blank box would silently create an unpaid agent.
      ...(commission.trim() === '' ? {} : { commission_rate: Number(commission) }),
    }),
    onSuccess: () => {
      setEmail(''); setFullName(''); setWhatsapp(''); setCommission(''); setRoleIds(new Set()); setPassword(genPassword());
      onDone();
    },
  });
  const valid = /\S+@\S+\.\S+/.test(email) && password.length >= 8 && fullName.trim().length > 0;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <h2 className="text-sm font-semibold text-gray-800 mb-1">{t('Créer un utilisateur')}</h2>
      <p className="text-[11.5px] text-gray-400 mb-4">
        {t('Le mot de passe est chiffré (bcrypt) par Supabase — notez-le maintenant, il ne sera plus jamais affiché.')}
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-[11px] font-semibold text-gray-500 mb-1">Email *</label>
          <input className={inputCls} type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="agent@storiz.ma" autoComplete="off" />
        </div>
        <div>
          <label className="block text-[11px] font-semibold text-gray-500 mb-1">{t('Mot de passe *')}</label>
          <div className="flex gap-2">
            <input className={inputCls} type={showPw ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />
            <button type="button" className="px-2 text-[11px] text-gray-500 underline shrink-0" onClick={() => setShowPw((v) => !v)}>
              {showPw ? t('Masquer') : t('Afficher')}
            </button>
            <button type="button" className="px-2 text-[11px] text-brand-600 underline shrink-0" onClick={() => { setPassword(genPassword()); setShowPw(true); }}>
              {t('Générer')}
            </button>
          </div>
        </div>
        <div>
          <label className="block text-[11px] font-semibold text-gray-500 mb-1">{t('Nom complet *')}</label>
          <input className={inputCls} value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Salma B." />
        </div>
        <div>
          <label className="block text-[11px] font-semibold text-gray-500 mb-1">WhatsApp</label>
          <input className={inputCls} value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)} placeholder="06XXXXXXXX" />
        </div>
        <div>
          <label className="block text-[11px] font-semibold text-gray-500 mb-1">{t('Commission par album (MAD)')}</label>
          <input
            className={inputCls} type="number" min="0" step="0.5" inputMode="decimal"
            value={commission} onChange={(e) => setCommission(e.target.value)}
            placeholder={t('Vide = tarif général')}
          />
          <p className="text-[10.5px] text-gray-400 mt-1">
            {t('Laisser vide pour le tarif général. Mettre 0 pour un compte qui ne gagne rien (ex. assistant automatique).')}
          </p>
        </div>
      </div>
      <div className="mt-3">
        <label className="block text-[11px] font-semibold text-gray-500 mb-1.5">{t('Rôles')}</label>
        <RoleCheckboxes allRoles={allRoles} selected={roleIds} onChange={setRoleIds} />
      </div>
      <div className="mt-4 flex items-center gap-3">
        <Button size="sm" onClick={() => createMut.mutate()} loading={createMut.isPending} disabled={!valid}>
          {t('Créer le compte')}
        </Button>
        {createMut.isError && <span className="text-[11.5px] text-red-600">{(createMut.error as Error).message}</span>}
        {createMut.isSuccess && <span className="text-[11.5px] text-green-600">✓ {t('Compte créé')}</span>}
      </div>
    </div>
  );
}

function UserRow({ user, allRoles, meId }: { user: StaffUser; allRoles: StaffRole[]; meId?: string }) {
  const { t } = useT();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [showPerf, setShowPerf] = useState(false);
  const [roleIds, setRoleIds] = useState<Set<string>>(new Set(user.roles.map((r) => r.id)));
  const [newPw, setNewPw] = useState('');
  // Numeric from PostgREST can arrive as a string; '' means "no override".
  const [commission, setCommission] = useState(
    user.commission_rate === null || user.commission_rate === undefined ? '' : String(user.commission_rate),
  );

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['staff-users'] });
  const updateMut = useMutation({
    mutationFn: (data: Parameters<typeof usersApi.update>[1]) => usersApi.update(user.id, data),
    onSuccess: () => { setEditing(false); setNewPw(''); invalidate(); },
  });

  return (
    <div className="border border-gray-100 rounded-xl p-4 bg-white">
      <div className="flex items-center gap-3 flex-wrap">
        <div
          className="w-9 h-9 rounded-full flex items-center justify-center text-[13px] font-bold shrink-0"
          style={{ background: user.is_active ? '#C9A24A22' : 'var(--color-adm-surface-3)', color: user.is_active ? 'var(--color-adm-gold-ink)' : 'var(--color-adm-faint)' }}
        >
          {(user.full_name ?? user.email ?? '?').slice(0, 1).toUpperCase()}
        </div>
        <div className="min-w-0">
          <p className="text-[13.5px] font-semibold text-gray-800 truncate">
            {user.full_name ?? '—'}
            {!user.is_active && <span className="ml-2 text-[10px] font-bold text-red-500 uppercase">{t('désactivé')}</span>}
          </p>
          <p className="text-[11.5px] text-gray-400 truncate">
            {user.email}{user.whatsapp ? ` · 📱 ${user.whatsapp}` : ''}
            {/* Only shown when an override exists — an agent on the normal rate
                stays visually unchanged. Number(0) is falsy, so the check is
                against null/undefined, never truthiness. */}
            {user.commission_rate !== null && user.commission_rate !== undefined && (
              <span className="ml-1.5 text-[10.5px] font-semibold" style={{ color: Number(user.commission_rate) === 0 ? 'var(--color-adm-faint)' : 'var(--color-adm-gold-ink)' }}>
                · {Number(user.commission_rate) === 0 ? t('sans commission') : `${Number(user.commission_rate)} MAD/album`}
              </span>
            )}
          </p>
        </div>
        <div className="flex-1" />
        <RoleChips roles={user.roles} />
        <button
          className="text-[11.5px] font-semibold underline underline-offset-2"
          style={{ color: showPerf ? 'var(--color-adm-gold-ink)' : '#C9A24A' }}
          onClick={() => setShowPerf((v) => !v)}
        >
          📊 {showPerf ? t('Fermer perf.') : t('Performance')}
        </button>
        <button className="text-[11.5px] text-gray-500 underline underline-offset-2 hover:text-gray-800" onClick={() => setEditing((v) => !v)}>
          {editing ? t('Fermer') : t('Modifier')}
        </button>
      </div>

      {showPerf && <AgentPerformance userId={user.id} />}

      {editing && (
        <div className="mt-3 pt-3 border-t border-gray-100 space-y-3">
          <div>
            <p className="text-[11px] font-semibold text-gray-500 mb-1.5">{t('Rôles')}</p>
            <RoleCheckboxes allRoles={allRoles} selected={roleIds} onChange={setRoleIds} />
          </div>
          <div className="flex items-end gap-2 flex-wrap">
            <div className="w-full sm:w-auto sm:min-w-[220px]">
              <label className="block text-[11px] font-semibold text-gray-500 mb-1">{t('Nouveau mot de passe (optionnel)')}</label>
              <input className={inputCls} type="text" value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder={t('Laisser vide pour ne pas changer')} autoComplete="new-password" />
            </div>
            <div className="w-full sm:w-auto sm:min-w-[200px]">
              <label className="block text-[11px] font-semibold text-gray-500 mb-1">{t('Commission par album (MAD)')}</label>
              <input
                className={inputCls} type="number" min="0" step="0.5" inputMode="decimal"
                value={commission} onChange={(e) => setCommission(e.target.value)}
                placeholder={t('Vide = tarif général')}
              />
            </div>
            <Button
              size="sm"
              onClick={() => updateMut.mutate({
                role_ids: [...roleIds],
                ...(newPw ? { new_password: newPw } : {}),
                // null clears the override → back to the global rate. An
                // explicit 0 is kept, which is how the bot earns nothing.
                commission_rate: commission.trim() === '' ? null : Number(commission),
              })}
              loading={updateMut.isPending}
            >
              {t('Enregistrer')}
            </Button>
            {user.id !== meId && (
              <button
                className={`text-[11.5px] underline underline-offset-2 px-2 py-1.5 ${user.is_active ? 'text-[#DC2626] dark:text-status-stuck' : 'text-[#16A34A] dark:text-status-done'}`}
                onClick={() => updateMut.mutate({ is_active: !user.is_active })}
              >
                {user.is_active ? t('Désactiver le compte') : t('Réactiver le compte')}
              </button>
            )}
            {updateMut.isError && <span className="text-[11.5px] text-red-600">{(updateMut.error as Error).message}</span>}
          </div>
        </div>
      )}
    </div>
  );
}


// ─── Agent performance dashboard (2026-07-21, admin-only) ────────────────────
// Data-dense on purpose: KPI strip, earnings with the ONE action (Non payé →
// Envoyé modal), pipeline of their confirmed orders, actives + activity feed.
const AGENT_STATUS_FR: Record<string, string> = {
  'pending-album': 'Attente album', 'album-submitted': 'Album soumis', paid: 'Payée',
  'ready-to-print': 'Prêt à imprimer', 'in-production': 'En production',
  printed: 'Imprimée', 'ready-to-deliver': 'Prêt à livrer', shipped: 'Expédiée', delivered: 'Livrée',
  cancelled: 'Annulée', 'in-redo': 'Réimpression',
};
const fmtDH = (n: number) => `${Math.round(n * 100) / 100} DH`;
const fmtDur = (min: number | null) => {
  if (min == null) return '—';
  if (min < 60) return `${min} min`;
  if (min < 48 * 60) return `${Math.round(min / 60 * 10) / 10} h`;
  return `${Math.round(min / 1440 * 10) / 10} j`;
};

function AgentPerformance({ userId }: { userId: string }) {
  const { t } = useT();
  const [payoutOpen, setPayoutOpen] = useState(false);
  const q = useQuery({ queryKey: ['agent-dashboard', userId], queryFn: () => usersApi.agentDashboard(userId) });
  const d = q.data;

  if (q.isLoading) return <div className="mt-3 pt-3 border-t border-gray-100 text-[12px] text-gray-400">{t('Chargement…')}</div>;
  if (!d) return <div className="mt-3 pt-3 border-t border-gray-100 text-[12px] text-red-500">{t('Impossible de charger les stats')}</div>;

  const kpi = (label: string, value: string) => (
    <div className="rounded-lg px-3 py-2" style={{ background: '#C9A24A0D', border: '1px solid #C9A24A22' }}>
      <p className="text-[15px] font-bold text-gray-800 leading-tight">{value}</p>
      <p className="text-[10px] text-gray-500">{label}</p>
    </div>
  );

  return (
    <div className="mt-3 pt-3 border-t border-gray-100 space-y-3">
      {/* KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {kpi(t('Commandes confirmées'), String(d.confirmed_count))}
        {kpi(t('Conversion → livrée'), d.conversion.rate_pct != null ? `${d.conversion.rate_pct}%` : '—')}
        {kpi(t('⌀ création → confirmation'), fmtDur(d.avg_creation_to_confirm_minutes))}
        {kpi(t('Actives maintenant'), String(d.active_orders.length))}
      </div>

      {/* Earnings — the ONE action on this panel is the amber Non payé button. */}
      <div className="flex items-center gap-3 flex-wrap rounded-xl px-4 py-3 bg-[#FBF7EF] dark:bg-adm-gold-wash" style={{ border: '1px solid #C9A24A33' }}>
        <span className="text-[12px] text-gray-600">💰 {t('Gains')}</span>
        <span className="text-[13px] font-bold text-gray-800">{t('Total')} {fmtDH(d.earnings.total)}</span>
        <span className="text-[12px] font-semibold text-[#0f7a4f] dark:text-status-done">{t('Payé')} {fmtDH(d.earnings.paid)}</span>
        <div className="flex-1" />
        <button
          onClick={() => d.earnings.unpaid > 0 && setPayoutOpen(true)}
          disabled={d.earnings.unpaid <= 0}
          className="px-4 py-2 rounded-xl text-[12.5px] font-bold text-white shadow-sm disabled:opacity-40"
          style={{ background: 'linear-gradient(135deg,#D97706,#B45309)' }}
          title={d.earnings.unpaid > 0 ? t('Cliquer pour marquer comme envoyé (reçu requis)') : t('Rien à payer')}
        >
          {t('NON PAYÉ')} · {fmtDH(d.earnings.unpaid)}{d.earnings.unpaid_count > 0 ? ` (${d.earnings.unpaid_count})` : ''}
        </button>
      </div>

      {/* Pipeline of their confirmed orders */}
      {d.confirmed_count > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap text-[11px]">
          {Object.entries(d.pipeline).map(([st, n]) => (
            <span key={st} className={`px-2 py-1 rounded-md font-medium ${st === 'delivered'
              ? 'bg-[rgba(16,185,129,.10)] text-[#0f7a4f] dark:bg-status-done-bg dark:text-status-done'
              : st === 'cancelled'
                ? 'bg-[rgba(220,38,38,.08)] text-[#b91c1c] dark:bg-status-stuck-bg dark:text-status-stuck'
                : 'bg-adm-surface-3 text-[#4B5563] dark:text-adm-ink-2'}`}>
              {AGENT_STATUS_FR[st] ?? st} <b>{n}</b>
            </span>
          ))}
        </div>
      )}

      {/* Actives + activity, side by side */}
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="rounded-lg border border-gray-100 p-2.5">
          <p className="text-[10.5px] font-bold uppercase tracking-wide text-gray-400 mb-1.5">{t('Commandes actives')}</p>
          {d.active_orders.length === 0 ? (
            <p className="text-[11.5px] text-gray-400">{t('Aucune commande en cours')}</p>
          ) : d.active_orders.map((o) => (
            <a key={o.shopify_order_id} href={`/admin/orders/${o.shopify_order_id}`}
              className="flex items-baseline gap-2 py-1 text-[12px] hover:underline">
              <span className="font-semibold text-gray-800 shrink-0">{o.name ?? `#${o.shopify_order_id}`}</span>
              <span className="truncate text-gray-500">{o.customer ?? ''}</span>
              <span className="ml-auto shrink-0 text-[10.5px] text-gray-400">{AGENT_STATUS_FR[o.status] ?? o.status}</span>
            </a>
          ))}
        </div>
        <div className="rounded-lg border border-gray-100 p-2.5">
          <p className="text-[10.5px] font-bold uppercase tracking-wide text-gray-400 mb-1.5">{t('Activité récente')}</p>
          {d.activity.length === 0 ? (
            <p className="text-[11.5px] text-gray-400">{t('Aucune activité')}</p>
          ) : d.activity.slice(0, 12).map((a, i) => (
            <p key={i} className="py-0.5 text-[11.5px] text-gray-600 truncate">
              <span className="text-gray-400 tabular-nums">{new Date(a.at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
              {' '}{a.summary ?? a.action}{a.entity_id ? ` · ${a.entity_id.slice(0, 18)}` : ''}
            </p>
          ))}
        </div>
      </div>

      {/* Payout history */}
      {d.payouts.length > 0 && (
        <div className="rounded-lg border border-gray-100 p-2.5">
          <p className="text-[10.5px] font-bold uppercase tracking-wide text-gray-400 mb-1.5">{t('Historique des paiements')}</p>
          {d.payouts.map((p) => (
            <div key={p.id} className="flex items-baseline gap-2 py-0.5 text-[11.5px] text-gray-600">
              <span className="tabular-nums text-gray-400">{new Date(p.created_at).toLocaleDateString('fr-FR')}</span>
              <span className="font-semibold text-gray-800">{fmtDH(Number(p.amount))}</span>
              <span>{t('{n} commandes', { n: p.order_count })}</span>
              <a href={p.receipt_url} target="_blank" rel="noopener noreferrer" className="ml-auto underline text-[10.5px] text-adm-gold-ink">
                {t('Reçu')}
              </a>
            </div>
          ))}
        </div>
      )}

      {payoutOpen && d.earnings.unpaid > 0 && (
        <PayoutModal
          userId={userId}
          agentName={d.agent.full_name ?? d.agent.email ?? ''}
          amount={d.earnings.unpaid}
          count={d.earnings.unpaid_count}
          onClose={() => setPayoutOpen(false)}
        />
      )}
    </div>
  );
}

// "Envoyé" modal — the ONLY path that pays commissions + writes the expense.
// Receipt is mandatory; confirm is disabled until a file is attached.
function PayoutModal({ userId, agentName, amount, count, onClose }: {
  userId: string; agentName: string; amount: number; count: number; onClose: () => void;
}) {
  const { t } = useT();
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const confirm = async () => {
    if (!file || busy) return;
    setBusy(true); setErr(null);
    try {
      const ct = file.type || 'image/jpeg';
      const { upload_url, storage_path, content_type } = await usersApi.agentPayoutUploadUrl(userId, file.name, ct);
      await uploadToSignedUrl(upload_url, file, content_type);
      const res = await usersApi.agentPayout(userId, storage_path);
      if (res.expense_error) setErr(t('Payé, mais écriture comptable en erreur : {m}', { m: res.expense_error }));
      setDone(true);
      queryClient.invalidateQueries({ queryKey: ['agent-dashboard', userId] });
      setTimeout(onClose, res.expense_error ? 4000 : 1200);
    } catch (e) {
      setErr((e as Error)?.message ?? t('Échec du paiement'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4" style={{ background: 'rgba(17,17,17,.35)' }} onClick={onClose}>
      <div className="w-full max-w-[420px] rounded-2xl bg-white p-5 shadow-2xl space-y-3" onClick={(e) => e.stopPropagation()}>
        <p className="text-[15px] font-bold text-gray-800">💸 {t('Marquer comme envoyé')}</p>
        <p className="text-[12.5px] text-gray-600">
          {t('{amount} DH · {n} commande(s) confirmée(s) — {name}', { amount: Math.round(amount * 100) / 100, n: count, name: agentName })}
        </p>
        <p className="text-[11.5px] text-gray-500">
          {t('Joignez le reçu du virement (obligatoire). À la confirmation : le montant passe en « Payé » et une dépense « Commission » est créée dans la comptabilité.')}
        </p>
        <input ref={fileRef} type="file" accept="image/*,.pdf" className="hidden"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
        <button
          onClick={() => fileRef.current?.click()}
          className={`w-full py-2.5 rounded-xl text-[12.5px] font-semibold ${file
            ? 'bg-[rgba(16,185,129,.06)] text-[#0f7a4f] dark:bg-status-done-bg dark:text-status-done'
            : 'bg-transparent text-adm-gold-ink'}`}
          style={{ border: '1.5px dashed #C9A24A88' }}
        >
          {file ? `✓ ${file.name}` : t('📎 Joindre le reçu de paiement')}
        </button>
        {err && <p className="text-[11.5px] text-red-600">{err}</p>}
        {done && !err && <p className="text-[11.5px] font-semibold text-[#0f7a4f] dark:text-status-done">✓ {t('Payé — comptabilisé')}</p>}
        <div className="flex justify-end gap-2 pt-1">
          <Button size="sm" variant="ghost" onClick={onClose} disabled={busy}>{t('Annuler')}</Button>
          <Button size="sm" onClick={confirm} loading={busy} disabled={!file || done}>
            {t('Confirmer l’envoi')}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Role builder (RBAC stage c, 2026-07-17) — Discord-style toggles ─────────
const PERM_LABELS: Record<string, string> = {
  'orders.view': 'Voir les commandes', 'orders.edit_status': 'Changer le statut',
  'orders.edit_customer': 'Modifier les infos client', 'orders.notes': 'Notes internes',
  'orders.create_manual': 'Créer / ajouter des albums', 'orders.price_correction': 'Corriger le prix',
  'orders.delete': 'Supprimer des commandes', 'orders.ban_client': 'Bannir des clients',
  'customers.view': 'Voir les clients (CRM)',
  'editor.access': "Accéder à l'éditeur", 'editor.ai_magic': 'AI Magic / régénération',
  'editor.upload_links': "Liens d'upload / reprise", 'photos.manage': 'Gérer les photos client',
  'covers.view': 'Voir les couvertures', 'covers.manage': 'Gérer les couvertures',
  'templates.view': 'Voir les modèles', 'templates.manage': 'Gérer les modèles',
  'production.pdf': 'PDF & intégrité impression', 'production.preview_links': 'Aperçus 3D client',
  'production.print_batches': "Lots d'impression", 'delivery.manage': 'Livraison AMEEX',
  'finance.view': 'Voir profit / coûts / marges', 'accounting.view': 'Voir la comptabilité',
  'accounting.edit': 'Modifier la comptabilité', 'marketing.manage': 'Marketing (dépenses pub)',
  'followups.view': 'Voir les relances', 'followups.send': 'Envoyer des relances',
  'settings.manage': 'Paramètres', 'stock.manage': 'Stock', 'users.manage': 'Gérer les utilisateurs',
  'roles.manage': 'Gérer les rôles', 'audit.view': "Journal d'activité", 'dashboard.view': 'Tableau de bord',
  'coupons.manage': 'Gérer les coupons (campagnes, designs, codes)',
};
const AREA_LABELS: Record<string, string> = {
  orders: '📦 Commandes', customers: '👤 Clients', editor: '🎨 Éditeur', photos: '🖼 Photos',
  covers: '📕 Couvertures', templates: '📐 Modèles', production: '🖨 Production',
  delivery: '🚚 Livraison', finance: '💰 Finance', accounting: '💰 Comptabilité',
  marketing: '📣 Marketing', coupons: '🎟 Coupons', followups: '💬 Relances', settings: '⚙️ Système',
  stock: '⚙️ Système', users: '⚙️ Système', roles: '⚙️ Système', audit: '⚙️ Système', dashboard: '⚙️ Système',
};

function RoleEditor({ role, registry, onChanged }: { role: StaffRole; registry: string[]; onChanged: () => void }) {
  const { t } = useT();
  const [perms, setPerms] = useState<Set<string> | null>(null);
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    usersApi.rolePermissions(role.id).then((r) => setPerms(new Set(r.permissions)));
  }, [role.id]);
  const saveMut = useMutation({
    mutationFn: () => usersApi.updateRole(role.id, { permissions: [...(perms ?? [])] }),
    onSuccess: () => { setDirty(false); onChanged(); },
  });
  const delMut = useMutation({ mutationFn: () => usersApi.deleteRole(role.id), onSuccess: onChanged });
  if (!perms) return <p className="text-[11px] text-gray-400 p-3">{t('Chargement…')}</p>;
  const isWildcard = perms.has('*');
  // group registry keys by area prefix
  const groups = new Map<string, string[]>();
  for (const p of registry) {
    const area = AREA_LABELS[p.split('.')[0]] ?? p.split('.')[0];
    groups.set(area, [...(groups.get(area) ?? []), p]);
  }
  return (
    <div className="p-3 space-y-3 border-t border-gray-100">
      {isWildcard && (
        <p className="text-[11.5px] font-semibold text-adm-gold-ink">
          ★ {t('Accès complet (toutes les permissions, actuelles et futures)')}
        </p>
      )}
      {!isWildcard && [...groups.entries()].map(([area, keys]) => (
        <div key={area}>
          <p className="text-[10.5px] font-bold uppercase tracking-wider text-gray-400 mb-1">{area}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
            {keys.map((k) => (
              <label key={k} className="flex items-center justify-between gap-2 py-1 cursor-pointer">
                <span className="text-[12px] text-gray-700">{PERM_LABELS[k] ?? k}</span>
                <input
                  type="checkbox"
                  className="accent-[#C9A24A] w-4 h-4"
                  checked={perms.has(k)}
                  onChange={(e) => {
                    const next = new Set(perms);
                    if (e.target.checked) next.add(k); else next.delete(k);
                    setPerms(next); setDirty(true);
                  }}
                />
              </label>
            ))}
          </div>
        </div>
      ))}
      <div className="flex items-center gap-3 pt-1">
        {!isWildcard && (
          <Button size="sm" onClick={() => saveMut.mutate()} loading={saveMut.isPending} disabled={!dirty}>
            {t('Enregistrer les permissions')}
          </Button>
        )}
        {!role.is_system && (
          <button
            className="text-[11.5px] text-red-600 underline underline-offset-2"
            onClick={() => { if (window.confirm(t('Supprimer ce rôle ? Les utilisateurs le perdront.'))) delMut.mutate(); }}
          >
            {t('Supprimer le rôle')}
          </button>
        )}
        {saveMut.isSuccess && !dirty && <span className="text-[11px] text-green-600">✓ {t('Enregistré — effectif sous 1 min')}</span>}
        {(saveMut.isError || delMut.isError) && <span className="text-[11px] text-red-600">{((saveMut.error || delMut.error) as Error).message}</span>}
      </div>
    </div>
  );
}

function RolesCard() {
  const { t } = useT();
  const queryClient = useQueryClient();
  const { data: rolesData } = useQuery({ queryKey: ['staff-roles'], queryFn: usersApi.roles, staleTime: 30_000 });
  const { data: regData } = useQuery({ queryKey: ['perm-registry'], queryFn: usersApi.permissionRegistry, staleTime: 300_000 });
  const [openId, setOpenId] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const createMut = useMutation({
    mutationFn: () => usersApi.createRole({ name: newName.trim(), permissions: [] }),
    onSuccess: (r) => { setNewName(''); queryClient.invalidateQueries({ queryKey: ['staff-roles'] }); setOpenId(r.role.id); },
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['staff-roles'] });
  const roles = rolesData?.roles ?? [];
  const registry = regData?.permissions ?? [];
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <h2 className="text-sm font-semibold text-gray-800 mb-1">{t('Rôles & permissions')}</h2>
      <p className="text-[11.5px] text-gray-400 mb-3">
        {t('Activez / désactivez chaque permission par rôle (style Discord). Les 5 rôles système sont modifiables mais pas supprimables.')}
      </p>
      <div className="space-y-1.5">
        {roles.map((r) => (
          <div key={r.id} className="border border-gray-100 rounded-lg">
            <button
              className="w-full flex items-center justify-between px-3 py-2 text-left"
              onClick={() => setOpenId(openId === r.id ? null : r.id)}
            >
              <span className="text-[13px] font-semibold text-gray-800">
                {r.name}
                {r.is_system && <span className="ml-2 text-[9.5px] font-bold uppercase text-gray-300 dark:text-adm-faint">{t('système')}</span>}
              </span>
              <span className="text-gray-400 text-xs">{openId === r.id ? '▲' : '▼'}</span>
            </button>
            {openId === r.id && <RoleEditor role={r} registry={registry} onChanged={refresh} />}
          </div>
        ))}
      </div>
      <div className="flex gap-2 mt-3">
        <input className={inputCls} placeholder={t('Nom du nouveau rôle (ex. Stagiaire)')} value={newName} onChange={(e) => setNewName(e.target.value)} />
        <Button size="sm" onClick={() => createMut.mutate()} loading={createMut.isPending} disabled={!newName.trim()}>
          {t('Créer le rôle')}
        </Button>
      </div>
      {createMut.isError && <p className="text-[11px] text-red-600 mt-1">{(createMut.error as Error).message}</p>}
    </div>
  );
}

export default function AdminUsersPage() {
  const { t } = useT();
  const { data: usersData, isLoading } = useQuery({ queryKey: ['staff-users'], queryFn: usersApi.list });
  const { data: rolesData } = useQuery({ queryKey: ['staff-roles'], queryFn: usersApi.roles, staleTime: 60_000 });
  const queryClient = useQueryClient();
  const allRoles = rolesData?.roles ?? [];
  const users = usersData?.users ?? [];

  return (
    <AdminLayout>
      <div className="max-w-4xl mx-auto space-y-4">
        <div>
          <h1 className="text-lg font-semibold text-gray-800" style={{ fontFamily: "'Fraunces', Georgia, serif" }}>
            {t('Utilisateurs & rôles')}
          </h1>
          <p className="text-[12px] text-gray-400 mt-0.5">
            {t("Comptes de l'équipe et leurs rôles. L'application des permissions par rôle arrive à l'étape suivante — d'ici là, les nouveaux comptes ne peuvent pas accéder aux données admin.")}
          </p>
        </div>

        <CreateUserCard allRoles={allRoles} onDone={() => queryClient.invalidateQueries({ queryKey: ['staff-users'] })} />

        <RolesCard />

        <div className="space-y-2">
          {isLoading && <p className="text-sm text-gray-400">{t('Chargement…')}</p>}
          {users.map((u) => <UserRow key={u.id} user={u} allRoles={allRoles} />)}
          {!isLoading && users.length === 0 && <p className="text-sm text-gray-400">{t('Aucun utilisateur')}</p>}
        </div>
      </div>
    </AdminLayout>
  );
}
